import React, { useMemo, useRef } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { useTheme, Box } from "@mui/material";

export default function Editor({ value, onChange, placeholder = "Start writing your content here..." }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const quillRef = useRef(null);

  // Custom image handler
  const imageHandler = useMemo(() => () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (file) {
        // Check file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          alert("Image size should be less than 5MB");
          return;
        }

        // Convert to base64 (in production, upload to server)
        const reader = new FileReader();
        reader.onload = (e) => {
          const imageUrl = e.target?.result;
          const quill = quillRef.current?.getEditor();
          if (quill) {
            const range = quill.getSelection(true);
            if (range) {
              quill.insertEmbed(range.index, "image", imageUrl, "user");
              quill.setSelection(range.index + 1);
            }
          }
        };
        reader.readAsDataURL(file);
      }
    };
  }, []);

  // Custom video handler
  const videoHandler = useMemo(() => () => {
    const url = prompt("Enter video URL (YouTube, Vimeo, or direct video link):");
    if (url) {
      const quill = quillRef.current?.getEditor();
      if (quill) {
        const range = quill.getSelection(true);
        if (range) {
          quill.insertEmbed(range.index, "video", url, "user");
          quill.setSelection(range.index + 1);
        }
      }
    }
  }, []);

  // Advanced toolbar configuration
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ font: [] }],
          [{ size: ["small", false, "large", "huge"] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ script: "sub" }, { script: "super" }],
          [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
          [{ direction: "rtl" }],
          [{ align: [] }],
          ["blockquote", "code-block"],
          ["link", "image", "video"],
          [{ table: true }],
          ["clean"],
        ],
        handlers: {
          image: imageHandler,
          video: videoHandler,
        },
      },
      table: true,
      clipboard: {
        matchVisual: false,
      },
    }),
    [imageHandler, videoHandler]
  );

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "script",
    "list",
    "bullet",
    "indent",
    "direction",
    "align",
    "blockquote",
    "code-block",
    "link",
    "image",
    "video",
    "table",
    "code",
  ];

  return (
    <Box
      sx={{
        "& .ql-container": {
          fontFamily: theme.typography.fontFamily,
          fontSize: "1rem",
          minHeight: "300px",
          borderRadius: "0 0 8px 8px",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"}`,
          backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "#ffffff",
          color: theme.palette.text.primary,
        },
        "& .ql-toolbar": {
          borderRadius: "8px 8px 0 0",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"}`,
          backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "#f8f9fa",
          borderBottom: "none",
          "& .ql-stroke": {
            stroke: theme.palette.text.primary,
          },
          "& .ql-fill": {
            fill: theme.palette.text.primary,
          },
          "& .ql-picker-label": {
            color: theme.palette.text.primary,
          },
          "& button:hover, & .ql-picker-label:hover": {
            backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
          },
          "& button.ql-active": {
            backgroundColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)",
          },
        },
        "& .ql-editor": {
          minHeight: "300px",
          "&.ql-blank::before": {
            color: theme.palette.text.secondary,
            fontStyle: "normal",
          },
          "& pre.ql-syntax": {
            backgroundColor: isDark ? "#1e1e1e" : "#f5f5f5",
            color: isDark ? "#d4d4d4" : "#1a1a1a",
            borderRadius: "4px",
            padding: "12px",
            overflow: "auto",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
            fontFamily: "'Fira Code', 'Courier New', monospace",
            fontSize: "0.9em",
            lineHeight: "1.5",
          },
          "& table": {
            borderCollapse: "collapse",
            width: "100%",
            margin: "12px 0",
            "& td, & th": {
              border: `1px solid ${isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"}`,
              padding: "8px 12px",
            },
            "& th": {
              backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
              fontWeight: "bold",
            },
            "& tr:nth-of-type(even)": {
              backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
            },
          },
          "& blockquote": {
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            paddingLeft: "16px",
            marginLeft: 0,
            fontStyle: "italic",
            color: theme.palette.text.secondary,
            backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
            padding: "12px 16px",
            borderRadius: "4px",
          },
          "& code": {
            backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
            padding: "2px 6px",
            borderRadius: "3px",
            fontFamily: "'Fira Code', 'Courier New', monospace",
            fontSize: "0.9em",
            color: isDark ? "#ff6b6b" : "#d63384",
          },
          "& img": {
            maxWidth: "100%",
            height: "auto",
            borderRadius: "8px",
            margin: "12px 0",
            boxShadow: isDark
              ? "0 4px 6px rgba(0,0,0,0.3)"
              : "0 2px 4px rgba(0,0,0,0.1)",
          },
          "& iframe": {
            maxWidth: "100%",
            borderRadius: "8px",
            margin: "12px 0",
          },
        },
      }}
    >
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ""}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
    </Box>
  );
}
